import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import AuthContext from '../context/AuthContext';
import { getReferralStats } from '../services/referralService';
import { WhatsappShareButton, TwitterShareButton, FacebookShareButton, WhatsappIcon, TwitterIcon, FacebookIcon } from 'react-share';
import { FiCopy, FiCheck, FiUsers, FiShoppingCart, FiDollarSign, FiTrendingUp, FiGift, FiShare2 } from 'react-icons/fi';

// Navbar Spacer for proper positioning
const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

// Hero Section – minimal, editorial
const HeroSection = styled.section`
  background: #0a0a0a;
  color: #fff;
  padding: 3.5rem 1.5rem 2.5rem;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 560px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 700;
  margin-bottom: 0.75rem;
  letter-spacing: -0.02em;
`;

const HeroSubtitle = styled.p`
  font-size: 0.95rem;
  line-height: 1.55;
  color: #a3a3a3;
`;

// Main Container
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #fff;
`;

// Referral Code Section
const ReferralCard = styled.div`
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 2rem;
  margin: -2rem auto 0;
  position: relative;
  z-index: 2;
  max-width: 720px;
  text-align: left;

  @media (max-width: 768px) {
    margin: -1.5rem 1rem 0;
    padding: 1.5rem 1.25rem;
  }
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #171717;
  margin-bottom: 0.25rem;
`;

const CardSubtitle = styled.p`
  color: #737373;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const ReferralLinkContainer = styled.div`
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 0;
  margin: 0 0 1.5rem;
  display: flex;
  align-items: stretch;
  gap: 0;
  min-height: 48px;
  overflow: hidden;

  @media (max-width: 640px) {
    flex-direction: column;
    min-height: auto;
  }
`;

const ReferralLink = styled.div`
  font-family: ui-monospace, 'SF Mono', 'Cascadia Mono', 'Consolas', monospace;
  font-size: 0.875rem;
  font-weight: 500;
  color: #171717;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0.875rem 1rem;
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 640px) {
    flex: 0 0 auto;
    padding: 0.75rem 1rem;
    width: 100%;
  }
`;

const CopyButton = styled.button`
  background: #171717;
  color: #fff;
  border: none;
  border-radius: 0 6px 6px 0;
  padding: 0 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover {
    background: #404040;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 640px) {
    border-radius: 0 0 6px 6px;
    padding: 0.75rem 1rem;
    justify-content: center;
  }
`;

// Stats Section
const StatsSection = styled.section`
  padding: 2.5rem 0 3rem;
  background: #fff;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  max-width: 720px;
  margin: 0 auto;

  @media (min-width: 640px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.25rem;
  }
`;

const StatCard = styled.div`
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 1.25rem 1rem;
  text-align: center;
`;

const StatIcon = styled.div`
  width: 36px;
  height: 36px;
  background: #171717;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.75rem;
  color: #fff;
  font-size: 1rem;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #171717;
  margin-bottom: 0.15rem;

  @media (min-width: 640px) {
    font-size: 1.75rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #737373;
  font-weight: 500;

  @media (min-width: 640px) {
    font-size: 0.85rem;
  }
`;

// Share Section
const ShareSection = styled.div`
  padding-top: 0.5rem;
  border-top: 1px solid #e5e5e5;
`;

const ShareTitle = styled.h3`
  font-size: 0.9rem;
  font-weight: 600;
  color: #737373;
  margin-bottom: 0.75rem;
  text-align: left;
`;

const ShareButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

// History Section
const HistorySection = styled.section`
  padding: 2rem 0 3rem;
  background: #fff;
`;

const HistoryContainer = styled.div`
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 1.5rem 1.25rem;
  max-width: 720px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const HistoryTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #171717;
  margin-bottom: 1.25rem;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HistoryTable = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  @media (max-width: 640px) {
    font-size: 0.8rem;
  }
`;

const TableHeader = styled.thead`
  background: #171717;
  color: #fff;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #fafafa;
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e5e5;
  vertical-align: middle;

  &:first-child {
    font-weight: 500;
    color: #171717;
  }

  @media (max-width: 640px) {
    padding: 0.6rem 0.75rem;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  @media (max-width: 640px) {
    padding: 0.6rem 0.75rem;
  }
`;

const StatusBadge = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    switch (props.status) {
      case 'converted':
        return `background: #dcfce7; color: #166534;`;
      case 'signed_up':
        return `background: #fef9c3; color: #854d0e;`;
      default:
        return `background: #f5f5f5; color: #525252;`;
    }
  }}
`;

const RewardBadge = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => props.earned ? '#dcfce7' : '#fef9c3'};
  color: ${props => props.earned ? '#166534' : '#854d0e'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2.5rem 1rem;
  color: #737373;
  font-size: 0.9rem;

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 0.75rem;
    opacity: 0.5;
  }
`;

const ReferralPage = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const referralLink = user ? `${window.location.origin}/register?ref=${user.email}` : '';

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getReferralStats();
        setStats(data);
      } catch (err) {
        setError(err.message || 'Failed to fetch referral stats');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchStats();
    }
  }, [user]);

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <PageContainer>
      <NavbarSpacer />
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>Refer & Earn</HeroTitle>
          <HeroSubtitle>
            Invite friends to join. You both get MAD coins when they sign up—and more when they buy.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <Container>
        {/* Referral Link Card */}
        <ReferralCard>
          <CardTitle>Your Referral Link</CardTitle>
          <CardSubtitle>
            You and your friend each get {stats?.rewardAmount || 100} MAD coins when they sign up. Share the link below.
          </CardSubtitle>

          <ReferralLinkContainer>
            <ReferralLink>{referralLink}</ReferralLink>
            <CopyButton onClick={handleCopy}>
              {copied ? (
                <>
                  <FiCheck />
                  Copied!
                </>
              ) : (
                <>
                  <FiCopy />
                  Copy Link
                </>
              )}
            </CopyButton>
          </ReferralLinkContainer>

          <ShareSection>
            <ShareTitle>Share</ShareTitle>
            <ShareButtons>
              <WhatsappShareButton url={referralLink} title="Check out TheMadZone">
                <WhatsappIcon size={32} round />
              </WhatsappShareButton>
              <TwitterShareButton url={referralLink} title="Check out TheMadZone">
                <TwitterIcon size={32} round />
              </TwitterShareButton>
              <FacebookShareButton url={referralLink} quote="Check out TheMadZone">
                <FacebookIcon size={32} round />
              </FacebookShareButton>
            </ShareButtons>
          </ShareSection>
        </ReferralCard>

        {/* Stats Section */}
        <StatsSection>
          <StatsGrid>
            <StatCard>
              <StatIcon>
                <FiUsers />
              </StatIcon>
              <StatValue>{stats?.referredCount || 0}</StatValue>
              <StatLabel>People Referred</StatLabel>
            </StatCard>

            <StatCard>
              <StatIcon>
                <FiShoppingCart />
              </StatIcon>
              <StatValue>{stats?.convertedCount || 0}</StatValue>
              <StatLabel>Completed Purchase</StatLabel>
            </StatCard>

            <StatCard>
              <StatIcon>
                <FiDollarSign />
              </StatIcon>
              <StatValue>{stats?.totalEarned || 0}</StatValue>
              <StatLabel>MAD Coins Earned</StatLabel>
            </StatCard>

            <StatCard>
              <StatIcon>
                <FiTrendingUp />
              </StatIcon>
              <StatValue>
                {stats?.referredCount > 0 ? Math.round((stats.convertedCount / stats.referredCount) * 100) : 0}%
              </StatValue>
              <StatLabel>Conversion Rate</StatLabel>
            </StatCard>
          </StatsGrid>
        </StatsSection>

        {/* History Section */}
        <HistorySection>
          <HistoryContainer>
            <HistoryTitle>
              <FiGift size={18} />
              Your Referral History
            </HistoryTitle>

            {stats?.referrals && stats.referrals.length > 0 ? (
              <HistoryTable>
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHeaderCell>Referred User</TableHeaderCell>
                      <TableHeaderCell>Sign-up Date</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Reward</TableHeaderCell>
                    </tr>
                  </TableHeader>
                  <tbody>
                    {stats.referrals.map(r => (
                      <TableRow key={r._id}>
                        <TableCell>{[r.referred?.firstName, r.referred?.lastName].filter(Boolean).join(' ') || r.referred?.email || '—'}</TableCell>
                        <TableCell>{new Date(r.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <StatusBadge status={r.status ? String(r.status).toLowerCase().replace(/\s+/g, '_') : ''}>
                            {r.status === 'converted' ? 'Completed Purchase' :
                             r.status === 'signed_up' ? 'Signed Up' : r.status}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          <RewardBadge earned={r.reward_earned}>
                            {r.reward_earned ? 'Earned' : 'Pending'}
                          </RewardBadge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </HistoryTable>
            ) : (
              <EmptyState>
                <FiShare2 />
                <p>No referrals yet. Start sharing your referral link to earn rewards!</p>
              </EmptyState>
            )}
          </HistoryContainer>
        </HistorySection>
      </Container>
    </PageContainer>
  );
};

export default ReferralPage;
